import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Checkbox from "components/Inputs/Checkbox";
import useAuthentication from "hooks/useAuthentication";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { DATABASE_DOWNLOAD_ACCESS } from "utils/constants";
import { hasLimitLeft, haveAccess } from "utils/permissions";
import DownloadDBButton from "../../components/CatalogPage/DownloadDBButton";
import { GCPData } from "../../interfaces/GCPData";
import { Membership } from "../../interfaces/UserInterface";
import { generateCatalogSchema } from "./CatalogPageValidation";
import useMessage from "hooks/useMessageStatus";
import NPWSStatus from "utils/statuses";

// Helper to normalize subscription name to match package keys in subscriptionDetails.packages.
const normalizeSubscriptionName = (subscription: string) => {
  if (subscription === "yearly_unlimited") return "yearlyUnlimited";
  if (subscription === "yearly_limited") return "yearlyLimited";
  return subscription;
};

interface CatalogActionButtonsProps {
  isFormDirty: boolean;
  hasProducts: boolean;
  getDatabase: () => void;
  setGcpData: React.Dispatch<GCPData | null | string>;
  isTermsAccepted: boolean;
  setTermsAccepted: (isTermsAccepted: boolean) => void;
  allProducts?: number;
}

const CatalogPageActionButtons: React.FC<CatalogActionButtonsProps> = ({
  isFormDirty,
  getDatabase,
  hasProducts,
  setGcpData,
  isTermsAccepted,
  setTermsAccepted,
  allProducts,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();
  const { user, activeMembership } = useAuthentication();

  const getActiveMembership: () => Membership | undefined | null = () => {
    let returnValue: Membership | undefined | null;
    returnValue = user?.memberships?.find(
      (membership) => membership.id === localStorage.getItem("activeId")
    );
    if (!returnValue && user?.memberships) returnValue = user?.memberships[0];
    return returnValue;
  };

  const {
    formState: { isValid },
  } = useForm({
    validationSchema: generateCatalogSchema(t, setGcpData, executeRecaptcha, getActiveMembership()),
    reValidateMode: "onSubmit",
  });

  const [showWarningOverLimit, setShowWarningOverLimit] = useState(false);
  const [messageSet, setMessageSet] = useState(false); // Flag to track message status

  const { setMessage, removeMessage } = useMessage();

  // Calculate used credits as the sum of CSV and API usage.
  const usedCredits =
    (activeMembership?.company?.subscriptionDetails?.counters?.csv || 0) +
    (activeMembership?.company?.subscriptionDetails?.counters?.api || 0);

  // Compute available credits before download.
  // This is the credits remaining before considering the current query results.
  const availableCreditsBeforeDownload = (() => {
    const subscription = activeMembership?.company?.subscription;
    if (!subscription) return Infinity;
    const normalizedSubscription = normalizeSubscriptionName(subscription);
    // For "yearlyUnlimited", assume unlimited credits.
    if (normalizedSubscription === "yearlyUnlimited") return Infinity;
    const packages = activeMembership?.company?.subscriptionDetails?.packages;
    if (!packages || !(normalizedSubscription in packages)) {
      console.warn(`Invalid subscription type: ${subscription}`);
      return Infinity;
    }
    const packageInfo = packages[normalizedSubscription];
    const verifyLimit = packageInfo.api;
    return verifyLimit - usedCredits;
  })();

  // Compute remaining credits after subtracting the query results (allProducts).
  const resultsCount = allProducts || 0;
  const remainingAfterDownload = availableCreditsBeforeDownload - resultsCount;

  // Define two conditions:
  // Case 1: No credits available even before subtracting results.
  const isZeroLimit = availableCreditsBeforeDownload === 0;
  // Case 2: Some credits exist, but subtracting results makes them negative.
  const isOverLimit = remainingAfterDownload < 0;

  const dbButtonDisabled = ((!isFormDirty || !isValid) && !hasProducts);

  const handleDownload = () => {
    // Case: Free subscription.
    if (activeMembership?.company?.subscription === "free") {
      setMessage(NPWSStatus.NO_DOWNLOAD_FOR_THIS_PACKAGE);
      return;
    }

    // If subscription is unlimited, allow download.
    const normalizedSubscription = normalizeSubscriptionName(activeMembership?.company?.subscription || "");
    if (normalizedSubscription === "yearlyUnlimited") {
      getDatabase();
      return;
    }

    // Case 1: If there are zero credits available (even without considering results).
    if (isZeroLimit) {
      setMessage(NPWSStatus.NO_LIMIT_FOR_VERIFY);
      return;
    }

    // Case 2: Some credits available, but subtracting the query results puts you below zero.
    if (isOverLimit) {
      setMessage(NPWSStatus.OVERLIMIT_DOWNLOAD_DB);
      return;
    }

    // Otherwise, allow download.
    getDatabase();
  };

  return (
    <>
      <div className="col-12 mt-3 d-flex flex-column justify-content-end align-items-center">
        <div style={{ width: "20%" }}>
          <button
            className={`btn primary border bg-color-white txt-color-dusty-orange px-4 mb-3 ${
              !isFormDirty || (!activeMembership && !isTermsAccepted) ? "disabled" : ""
            }`}
            data-testid="searchButton"
            type="submit"
            style={{ width: "100%" }}>
            {t("search")}
          </button>
        </div>
        <DownloadDBButton
          disabled={dbButtonDisabled}
          company={activeMembership?.company}
          getDatabase={handleDownload}
          showWarningOverLimit={
            activeMembership?.company?.subscription === "free" ||
            isZeroLimit ||
            isOverLimit
          }
        />
      </div>
    </>
  );
};

export default CatalogPageActionButtons;
